import React from 'react';
import { Download } from 'lucide-react';
import { SearchInput } from './filters/SearchInput';
import { FilterSelect } from './filters/FilterSelect';
import { FilterButton } from './filters/FilterButton';
import { severityOptions, statusOptions, timeframeOptions, sortOptions } from '../../data/findings-filters';

interface FindingsFiltersProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedSeverity: string;
  onSeverityChange: (severity: string) => void;
  selectedStatus: string;
  onStatusChange: (status: string) => void;
  selectedTimeframe: string;
  onTimeframeChange: (timeframe: string) => void;
  selectedSort?: string;
  onSortChange?: (sort: string) => void;
  selectedTags: string[];
  onTagChange: (tag: string, checked: boolean) => void;
  selectedAssignees: string[];
  onAssigneeChange: (assigneeId: string, checked: boolean) => void;
  onReset: () => void;
  onExport: () => void;
}

export const FindingsFilters: React.FC<FindingsFiltersProps> = ({
  searchQuery,
  onSearchChange,
  selectedSeverity,
  onSeverityChange,
  selectedStatus,
  onStatusChange,
  selectedTimeframe,
  onTimeframeChange,
  selectedSort = '-created_at',
  onSortChange = () => {},
  selectedTags,
  onTagChange,
  selectedAssignees,
  onAssigneeChange,
  onReset,
  onExport,
}) => {
  const filterSections = [
    {
      title: 'Tags',
      options: [
        { value: 'production', label: 'Production', color: 'text-red-500' },
        { value: 'staging', label: 'Staging', color: 'text-yellow-500' },
        { value: 'development', label: 'Development', color: 'text-blue-500' },
      ],
      selectedValues: selectedTags,
      onValueChange: onTagChange,
    },
    {
      title: 'Assignees',
      options: [
        { value: 'unassigned', label: 'Unassigned', color: 'text-gray-400' },
        { value: 'me', label: 'Assigned to me', color: 'text-blue-500' },
        { value: 'team', label: 'My team', color: 'text-purple-500' },
      ],
      selectedValues: selectedAssignees,
      onValueChange: onAssigneeChange,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        {/* Search */}
        <div className="flex-1">
          <SearchInput
            value={searchQuery}
            onChange={onSearchChange}
            placeholder="Search by title, description, or ID..."
          />
        </div>

        {/* Quick Filters */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="w-48">
            <FilterSelect
              value={selectedSeverity}
              onValueChange={onSeverityChange}
              options={severityOptions}
              placeholder="All Severities"
              label="Severity"
            />
          </div>

          <div className="w-48">
            <FilterSelect
              value={selectedStatus}
              onValueChange={onStatusChange}
              options={statusOptions}
              placeholder="All Statuses"
              label="Status"
            />
          </div>

          <div className="w-48">
            <FilterSelect
              value={selectedTimeframe}
              onValueChange={onTimeframeChange}
              options={timeframeOptions}
              placeholder="All Time"
              label="Timeframe"
            />
          </div>

          <div className="w-48">
            <FilterSelect
              value={selectedSort}
              onValueChange={onSortChange}
              options={sortOptions}
              placeholder="Sort By"
              label="Sort By"
            />
          </div>

          <FilterButton sections={filterSections} onReset={onReset} />

          {/* <button
            onClick={onExport}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-500/10 text-blue-500 rounded-lg hover:bg-blue-500/20 transition-colors"
          >
            <Download className="h-4 w-4" />
            <span className="text-sm">Export</span>
          </button> */}
        </div>
      </div>

      {/* Active Filters */}
      {(selectedTags.length > 0 || selectedAssignees.length > 0) && (
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <span>Active filters:</span>
          {/* Render active filter pills here */}
        </div>
      )}
    </div>
  );
};